import React, { useEffect, useState } from 'react';
import { Input, Select, message, Spin } from 'antd';
import { PrivateAxiosInstance } from '../../../services/createAxiosInstance';
import { URLs } from '../../../constants/apiEndpoints';
import Pagination from '../../../components/Pagination/Pagination';
import { Button } from '@nextui-org/react';
import { SiMicrosoftexcel } from 'react-icons/si';
import jsonAsXlsx from 'json-as-xlsx';
import { DatePicker } from '@nextui-org/react';
import { FaSearch } from 'react-icons/fa';
import { GrPowerReset } from 'react-icons/gr';

const { Search } = Input;
const { Option } = Select;

function AllUsers() {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [kycStatusFilter, setKycStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [date, setDate] = useState();

  const getUserDetails = async (
    page = 1,
    limit = 20,
    searchValue = '',
    kycStatusFilter = '',
    date = ''
  ) => {
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        email: searchValue,
        customVerify: kycStatusFilter,
        createdAt: date ? `${date.year}-${date.month}-${date.day}` : '',
      }).toString();

      const res = await PrivateAxiosInstance.get(
        `${URLs.GET_ALL_USERS_URL}?${queryParams}`
      );

      const { users, totalPages, total } = res?.data || {
        users: [],
        totalPages: 0,
      };
      setFilteredData(users);
      setTotalPages(totalPages);
      setTotalUsers(total);
    } catch (error) {
      message.error('Failed to fetch user details');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserDetails(currentPage, 20, searchText, kycStatusFilter, date);
  }, [currentPage, searchText, kycStatusFilter, date]);

  const tableHeaders = [
    'User Id',
    'Name',
    'Email',
    'Mobile no',
    'KYC Status',
    'Regi date',
  ];

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleKycStatusChange = (value) => {
    setKycStatusFilter(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const exportUsersToExcel = async () => {
    setExportLoading(true);
    let allData = [];
    let page = 1;

    try {
      // Loop through all pages to fetch all user data
      let res;
      do {
        const queryParams = new URLSearchParams({
          page: String(page),
          limit: '60',
          email: searchText,
          customVerify: kycStatusFilter,
          createdAt: date ? `${date.year}-${date.month}-${date.day}` : '',
        }).toString();

        res = await PrivateAxiosInstance.get(
          `${URLs.GET_ALL_USERS_URL}?${queryParams}`
        );
        const users = res?.data?.users || [];
        allData = [...allData, ...users]; // Collect data from each page
        page += 1; // Increment page to fetch next
      } while (res?.data?.users?.length > 0); // Continue until there's no data left to fetch

      if (allData.length === 0) {
        message.error('No data to export');
        return;
      }

      const exportData = [
        {
          sheet: `Users-${date ? `${date.year}-${date.month}-${date.day}` : 'all'}`,
          columns: [
            { label: 'User Id', value: 'preferred_username' },
            { label: 'Name', value: 'name' },
            { label: 'Email', value: 'email' },
            { label: 'Mobile no', value: 'phone_number' },
            { label: 'KYC Status', value: 'custom:Verify' },
            { label: 'Regi date', value: 'createdAt' },
          ],
          // Prepare the content with actual data mapped to the columns
          content: allData.map((user) => ({
            preferred_username: user.preferred_username
              ? user.preferred_username
              : '-',
            name: user.name ? user.name : '-',
            email: user.email ? user.email : '-',
            phone_number: user.phone_number ? user.phone_number : '-',
            'custom:Verify':
              user['custom:Verify'] === 'verified'
                ? 'Verified'
                : 'Not Verified',
            createdAt: user.createdAt
              ? new Date(user.createdAt).toLocaleDateString()
              : '-',
          })),
        },
      ];

      // Trigger Excel export
      jsonAsXlsx(exportData, {
        fileName: `Users-${date ? `${date.year}-${date.month}-${date.day}` : 'all'}`,
        writeOptions: { bookType: 'xlsx' },
      });
    } catch (error) {
      message.error('Failed to export user details');
      console.error('Error exporting users:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const resetFilter = () => {
    setSearchText('');
    setKycStatusFilter('');
    setDate(null);
  };

  return (
    <>
      <div className="font-poppins overflow-x-auto">
        <h2 className="text-2xl font-bold text-gray-200 mt-[75px]">
          Total Users ({totalUsers})
        </h2>
        <Spin spinning={loading} fullscreen />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-6 bg-background-secondary-color p-3 w-full rounded-t-lg mt-5">
          <div className="ml-[-20px] w-[300px] mb-3 md:ml-[0px] lg:ml-[0px] mt-3 sm:mt-0 sm:ml-[0px] group">
            <Search
              placeholder="Search by  email"
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
              className="custom-search-input"
              style={{ fontFamily: 'Poppins' }}
            />
          </div>

          <div className="w-full mb-2 mt-3 sm:mt-0 md:ml-[40px] group">
            <Select
              defaultValue=""
              style={{ width: 200 }}
              onChange={handleKycStatusChange}
            >
              <Option value="">All</Option>
              <Option value="verified">Verify</Option>
              <Option value="not_verify">Not Verify</Option>
            </Select>
          </div>

          <DatePicker
            className="max-w-sm"
            granularity="day"
            value={date}
            onChange={setDate}
          />

          <div className="w-40 flex md:justify-end items-center ">
            <Button
              onClick={exportUsersToExcel}
              isLoading={exportLoading}
              startContent={<SiMicrosoftexcel fill="black" size={20} />}
              className="bg-btn-color text-black cursor-pointer mt-3 md:mt-0"
            >
              Export
            </Button>
          </div>

          <div className="w-40 flex md:justify-end items-center ">
            <Button
              onClick={resetFilter}
              startContent={<GrPowerReset fill="black" size={20} />}
              className="bg-red-600 text-black cursor-pointer mt-3 md:mt-0"
            >
              Reset
            </Button>
          </div>
        </div>

        <table className="min-w-full bg-white">
          <thead className="bg-gray-950 whitespace-nowrap">
            <tr>
              {tableHeaders.map((header, index) => (
                <th
                  key={index}
                  className="p-4 text-left text-sm font-medium font-poppins text-white"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="whitespace-normal">
            {filteredData?.length > 0 ? (
              filteredData.map((data, index) => (
                <tr
                  key={index}
                  className="bg-gray-800 odd:bg-gray-700 cursor-pointer hover:bg-red-900"
                >
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.preferred_username || '-'}
                  </td>
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.name || '-'}
                  </td>
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.email || '-'}
                  </td>
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.phone_number || '-'}
                  </td>
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.['custom:Verify'] === 'verified' ? (
                      <span className="w-[78px] block font-poppins text-center py-1 border bg-green-500 border-green-500 text-white rounded text-xs">
                        Verify
                      </span>
                    ) : (
                      <span className="w-[78px] block font-poppins text-center py-1 border bg-red-500 border-red-500 text-white rounded text-xs">
                        Not Verify
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {' '}
                    {data?.createdAt
                      ? new Date(data.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={tableHeaders.length} className="text-center p-4">
                  No Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </>
  );
}

export default AllUsers;
